/**
 * /frontend/src/pages/dashboard/post/Post.js (v2.13)
 * ---------------------------------------------------------------
 * • FIX: Always fetch user on mount to prevent stale plan data from localStorage.
 * • NEW: Fetches full user object and passes it to PostForm.
 * • FIX: Avoid edit-screen +4h bug by waiting for user timezone to load.
 * • NEW: Added FeedbackPopup for weekly proactive feedback collection.
------------------------------------------------------------------- */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { parse } from 'date-fns';
import { zonedTimeToUtc, formatInTimeZone } from 'date-fns-tz';
import { Modal, Row, Col, Image, Button } from 'react-bootstrap';
import { Phone } from 'react-bootstrap-icons';

import { useLoading } from '../../../context/LoadingContext';

import AccountSelector      from './AccountSelector';
import PostForm             from './PostForm';
import NetworkPreviewHub    from './previews/NetworkPreviewHub';

import { publishFacebookPost }  from './facebook/FacebookPostService';
import { publishTwitterPost }   from './twitter/TwitterPostService';
import { publishLinkedInPost }  from './linkedin/LinkedInPostService';
import { publishThreadsPost }   from './threads/ThreadsPostService';
import { publishTikTokPost }    from './tiktok/TikTokPostService';
import { publishInstagramPost } from './instagram/InstagramPostService';
import { publishPinterestPost } from './pinterest/PinterestPostService';
import { publishYouTubePost }   from './youtube/YouTubePostService';

import PinterestBoardsModal   from './pinterest/PinterestBoardsModal';
import InstagramOptionsDrawer from './instagram/InstagramOptionsDrawer';
import FacebookOptionsDrawer  from './facebook/FacebookOptionsDrawer';
import TikTokOptionsDrawer    from './tiktok/TikTokOptionsDrawer';
import YouTubeOptionsDrawer   from './youtube/YouTubeOptionsDrawer';

import { getPlatformIcon } from '../accounts/helpers/platformIcons';
import { captionForNetwork } from '../../../utils/hashtags';

// NEW: Feedback Trigger Component
import FeedbackPopup from './components/FeedbackPopup';

/* -------------------------- helpers -------------------------- */
const VIDEO_EXT = ['.mp4', '.mov', '.avi', '.m4v', '.wmv'];
const inferDefaultFormat = p => (p === 'instagram' ? 'feed' : 'feed');

const getCurrentUser = () =>
  window.currentUser ||
  JSON.parse(localStorage.getItem('user') || '{}') ||
  {};

function toUtcDate(dbValue) {
  if (!dbValue) return null;
  return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dbValue)
    ? new Date(dbValue.replace(' ', 'T') + 'Z') // MySQL 'YYYY-MM-DD HH:mm:ss' as UTC
    : new Date(dbValue);                         // ISO-like
}

/* ---------------------- App promo modal ---------------------- */
const AppPromoModal = ({ show, handleClose, handleDownloadClick }) => {
  const appStoreBadge   = 'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg';
  const googlePlayBadge = 'https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg';
  const appStoreLink    = 'https://apps.apple.com/us/app/greatly-social/id1563430092';
  const googlePlayLink  = 'https://play.google.com/store/apps/details?id=com.app.greatlysocial';
  const appMockupImage  = '/assets/images/greatlysocialapp.png';

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <Phone className="me-2 text-primary" />
          Take Greatly Social With You
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="align-items-center">
          <Col md={5} className="d-none d-md-block text-center">
            <Image src={appMockupImage} alt="Greatly Social App Mockup" fluid style={{ maxHeight: 400 }} />
          </Col>
          <Col md={7} className="text-center text-md-start">
            <h3 className="fw-bold">Manage content on the go.</h3>
            <p className="lead text-muted">
              Get the full power of Greatly Social right in your pocket. Draft posts, reschedule, and manage your calendar from anywhere.
            </p>
            <div className='d-flex flex-column align-items-center align-items-md-start gap-3 mt-4'>
              <a href={appStoreLink} target="_blank" rel="noopener noreferrer" onClick={handleDownloadClick}>
                <Image src={appStoreBadge} alt='Download on the App Store' style={{ height: 50 }} />
              </a>
              <a href={googlePlayLink} target="_blank" rel="noopener noreferrer" onClick={handleDownloadClick}>
                <Image src={googlePlayBadge} alt='Get it on Google Play' style={{ height: 50 }} />
              </a>
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>Maybe Later</Button>
      </Modal.Footer>
    </Modal>
  );
};

/* ============================= Component ============================= */
export default function Post () {
  const { show, hide } = useLoading();
  const location       = useLocation();
  const navigate       = useNavigate();
  const editPostId     = location.state?.editPostId || null;
  const [editingId, setEditingId] = useState(editPostId);
  // Analytics › Top posts › 'Post again' (Part E, T29) opens a copy here.
  const postAgainDraft = location.state?.draft || null;
  const [draftNote, setDraftNote] = useState(null);

  /* ------------ user ------------ */
  const bootUser    = getCurrentUser();
  const [user, setUser] = useState(bootUser);
  const [userTz, setUserTz] = useState(bootUser.timezone || 'UTC');
  const [userLoaded, setUserLoaded] = useState(!!bootUser.id);

  /* ------------ post state ------------ */
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [caption, setCaption]                   = useState('');
  const [files, setFiles]                       = useState([]);   // [{key,url}]
  const [postType, setPostType]                 = useState('now');
  const [scheduleDateTime, setScheduleDateTime] = useState('');

  /* ------------ AI Best Time ------------ */
  const [isFindingTime, setIsFindingTime] = useState(false);

  /* ------------ Hashtags (Part E, T31): LinkedIn / Facebook trimming, default on ------------ */
  const [hashtagSettings, setHashtagSettings] = useState(null);
  useEffect(() => {
    axios.get('/api/hashtags/settings', { withCredentials: true })
      .then(({ data }) => { if (data && typeof data.trimLinkedInFacebook === 'boolean') setHashtagSettings(data); })
      .catch(() => {});   // unknown → the default (on)
  }, []);

  /* ------------ App Promo Modal ------------ */
  const [showAppPromo, setShowAppPromo] = useState(false);

  /* Promo decide */
  useEffect(() => {
    const linkClicked   = localStorage.getItem('appPromoLinkClicked');
    if (linkClicked) return;
    const lastDismissed = localStorage.getItem('appPromoDismissedTimestamp');
    if (lastDismissed) {
      const oneDay = 24 * 60 * 60 * 1000;
      const lastDismissedDate = new Date(parseInt(lastDismissed));
      if (Date.now() - lastDismissedDate.getTime() > oneDay) setShowAppPromo(true);
    } else {
      setShowAppPromo(true);
    }
  }, []);

  const handleModalClose = () => {
    setShowAppPromo(false);
    localStorage.setItem('appPromoDismissedTimestamp', Date.now().toString());
  };
  const handleModalDownloadClick = () => {
    setShowAppPromo(false);
    localStorage.setItem('appPromoLinkClicked', 'true');
  };

  /* THIS IS THE FIX: Always fetch the user on mount to get the latest plan. */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/api/auth/me', { withCredentials: true });
        if (data?.user?.id) {
          window.currentUser = data.user;
          localStorage.setItem('user', JSON.stringify(data.user));
          setUser(data.user);
          setUserTz(data.user.timezone || 'UTC');
          setUserLoaded(true);
        }
      } catch (err) {
        console.error("Failed to fetch user data:", err);
      }
    })();
  }, []);

  /* EDIT MODE: wait until userLoaded is true so we have the correct tz */
  useEffect(() => {
    if (!editPostId || !userLoaded) return;
    ;(async () => {
      try {
        show('Loading scheduled post…');
        const { data } = await axios.get('/api/posting/all-posts', { withCredentials:true });
        const p = data.find(item => String(item.id) === String(editPostId));
        if (!p) throw new Error('Post not found');

        setCaption(p.caption || '');
        if (p.media_url) setFiles([{ key:'orig', url:p.media_url, file:null }]);
        setPostType('schedule');

        const rawUtc        = p.scheduled_at || p.post_date;
        const utcDate       = toUtcDate(rawUtc);
        const localForInput = utcDate ? formatInTimeZone(utcDate, userTz, "yyyy-MM-dd'T'HH:mm") : '';
        setScheduleDateTime(localForInput);

        setSelectedAccounts([{
          id           : p.account_id,
          externalId   : p.account_id,
          displayName  : p.account_username,
          userAvatar   : p.user_avatar,
          platform     : p.platform,
          platformIcon : getPlatformIcon(p.platform),
          options      : typeof p.options === 'string' ? JSON.parse(p.options) : (p.options || {}),
        }]);

        setEditingId(editPostId);
        navigate(location.pathname, { replace:true, state: {} });
      } catch (err) {
        alert('Could not load the scheduled post for editing.');
      } finally { hide(); }
    })();
  }, [editPostId, userLoaded, userTz, navigate, show, hide]);

  /* POST AGAIN: prefill the caption (and the photo, when there is one) once */
  useEffect(() => {
    if (!postAgainDraft) return;
    setCaption(postAgainDraft.caption || '');
    if (postAgainDraft.mediaUrl) setFiles([{ key: 'post-again', url: postAgainDraft.mediaUrl, file: null }]);
    setDraftNote(postAgainDraft.note || 'Copied from one of your top posts.');
    navigate(location.pathname, { replace: true, state: {} });
  }, [postAgainDraft, navigate, location.pathname]);

  const updateAccountOption = useCallback((id, platform, opts) => {
    setSelectedAccounts(prev =>
      prev.map(a => (a.id === id && a.platform === platform)
        ? { ...a, options:{ ...a.options, ...opts } }
        : a
      )
    );
  }, []);

  const [showPinterestModal, setShowPinterestModal] = useState(false);
  const [currentPinterestId, setCurrentPinterestId] = useState(null);
  const [selectedBoardIds,   setSelectedBoardIds]   = useState([]);
  const [pinterestHandled,   setPinterestHandled]   = useState(false);

  useEffect(() => {
    const p = selectedAccounts.find(a => a.platform === 'pinterest');
    if (p) {
      setCurrentPinterestId(p.id);
      if (!pinterestHandled) setShowPinterestModal(true);
    } else {
      setShowPinterestModal(false);
      setCurrentPinterestId(null);
      setSelectedBoardIds([]);
      setPinterestHandled(false);
    }
  }, [selectedAccounts, pinterestHandled]);

  useEffect(() => {
    if (!files.length) return;
    const first   = files[0];
    const isVideo = VIDEO_EXT.some(e => first.url.toLowerCase().endsWith(e));
    const multi   = files.length > 1 && files.length <= 10;
    selectedAccounts
      .filter(a => a.platform === 'instagram')
      .forEach(acc => {
        const fmt    = acc.options?.format;
        const isAuto = acc.options?._auto;
        if (multi && fmt !== 'carousel' && fmt !== 'story') { updateAccountOption(acc.id, acc.platform, { format:'carousel', _auto:true }); return; }
        if (!multi && fmt === 'carousel' && isAuto) updateAccountOption(acc.id, acc.platform, { format:'feed', _auto:true });
        if (!multi && isVideo && fmt !== 'reel') updateAccountOption(acc.id, acc.platform, { format:'reel', _auto:true });
        if (!isVideo && fmt === 'reel' && isAuto) updateAccountOption(acc.id, acc.platform, { format:'feed', _auto:true });
      });
  }, [files, selectedAccounts, updateAccountOption]);

  const handleFindBestTime = async () => {
    if (selectedAccounts.length === 0)
      return alert('Please select at least one social account to get a time suggestion.');
    setIsFindingTime(true);
    try {
      const { data } = await axios.post('/api/ai/best-time', {
        platforms: selectedAccounts.map(acc => acc.platform),
        userTz
      }, { withCredentials: true });

      if (data.bestTime) {
        const localTime = formatInTimeZone(data.bestTime, userTz, "yyyy-MM-dd'T'HH:mm");
        setScheduleDateTime(localTime);
      } else {
        throw new Error('No time was returned from the server.');
      }
    } catch (err) {
      console.error('Failed to fetch best time:', err);
      alert('Could not find the best time to post. Please try again.');
    } finally {
      setIsFindingTime(false);
    }
  };

  const handleCreatePost = async e => {
    e.preventDefault();
    if (!selectedAccounts.length) return alert('Select at least one account');
    if (postType === 'schedule' && !scheduleDateTime) return alert('Please choose a date & time for the schedule');

    show(postType === 'now' ? 'Publishing post…' : 'Scheduling post…');

    for (const acc of selectedAccounts) {
      try {
        const firstFile = files[0];
        const mediaUrl  = firstFile ? firstFile.url : null;
        const isVideo   = mediaUrl ? VIDEO_EXT.some(ext => mediaUrl.toLowerCase().endsWith(ext)) : false;
        const format    = acc.options?.format || inferDefaultFormat(acc.platform);
        // LinkedIn / Facebook get only the first few hashtags unless the user switched that off.
        const text      = captionForNetwork(caption, acc.platform, hashtagSettings);

        if (postType === 'schedule') {
          const avatar  = acc.userAvatar || acc.profileImage || '';
          const api     = editingId ? 'put' : 'post';
          const url     = editingId ? `/api/posting/scheduled-posts/${editingId}` : '/api/posting/scheduled-posts';

          const parsed = parse(scheduleDateTime, "yyyy-MM-dd'T'HH:mm", new Date());
          const isoUtc = zonedTimeToUtc(parsed, userTz).toISOString();

          await axios[api](url, {
            accountId       : acc.externalId || acc.id,
            accountUsername : acc.displayName || '',
            platform        : acc.platform,
            caption         : text,
            mediaUrl        : mediaUrl || '',
            scheduledAt     : isoUtc,
            userAvatar      : avatar,
            platformIcon    : acc.platformIcon || '',
            options         : acc.options || {},
          }, { withCredentials:true });
          continue;
        }

        switch (acc.platform) {
          case 'facebook':
            await publishFacebookPost({ pageId: acc.externalId || acc.id, message: text, mediaUrl, isVideo, format });
            break;
          case 'instagram':
            if (!acc.externalId) throw new Error('Instagram Business Account ID (igId) is missing');
            await publishInstagramPost(acc.externalId, caption, files, format);
            break;
          case 'twitter':
          case 'x':
            await publishTwitterPost(acc.externalId || acc.id, caption, mediaUrl);
            break;
          case 'linkedin':
            await publishLinkedInPost(acc.id, text, files);
            break;
          case 'threads':
            await publishThreadsPost(acc.id, caption, files);
            break;
          case 'tiktok':
            await publishTikTokPost(acc.id, caption, files, acc.options);
            break;
          case 'pinterest':
            await publishPinterestPost(acc.id, selectedBoardIds, caption, caption, mediaUrl);
            break;
          case 'youtube':
            await publishYouTubePost(acc.id, caption, files, acc.options);
            break;
          default:
            console.warn(`Unsupported platform: ${acc.platform}`);
        }
      } catch (err) {
        alert(`Error posting to ${acc.displayName}: ${err.message}`);
      }
    }

    hide();
    resetForm();
    alert(postType === 'now' ? 'Post submitted!' : 'Post scheduled!');
  };

  const resetForm = () => {
    setSelectedAccounts([]);
    setCaption('');
    setFiles([]);
    setPostType('now');
    setScheduleDateTime('');
    setSelectedBoardIds([]);
    setPinterestHandled(false);
    setEditingId(null);
    setDraftNote(null);
  };

  const instagramAccounts = selectedAccounts.filter(a => a.platform === 'instagram');
  const facebookAccounts  = selectedAccounts.filter(a => a.platform === 'facebook');
  const tiktokAccounts    = selectedAccounts.filter(a => a.platform === 'tiktok');
  const youtubeAccounts   = selectedAccounts.filter(a => a.platform === 'youtube');

  return (
    <>
      <AppPromoModal
        show={showAppPromo}
        handleClose={handleModalClose}
        handleDownloadClick={handleModalDownloadClick}
      />

      {/* NEW: Proactive Feedback System */}
      <FeedbackPopup />

      <div className='container mt-4'>
        <div className='row'>
          <div className='col-md-6'>
            {draftNote && (
              <div role='status' data-testid='post-again-note' style={{ background: '#eff6ff', border: '1px solid #dbeafe', color: '#1e3a8a', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
                {draftNote}
              </div>
            )}
            <AccountSelector
              selectedAccounts={selectedAccounts}
              setSelectedAccounts={setSelectedAccounts}
              inferDefaultFormat={inferDefaultFormat}
            />
            <PostForm
              caption={caption} setCaption={setCaption}
              files={files}     setFiles={setFiles}
              postType={postType} setPostType={setPostType}
              scheduleDateTime={scheduleDateTime} setScheduleDateTime={setScheduleDateTime}
              handleCreatePost={handleCreatePost}
              selectedAccounts={selectedAccounts}
              instagramDrawer={
                <>
                  <InstagramOptionsDrawer instagramAccounts={instagramAccounts} updateAccountOption={updateAccountOption} filesCount={files.length}/>
                  <FacebookOptionsDrawer  facebookAccounts={facebookAccounts}  updateAccountOption={updateAccountOption} files={files}/>
                  <TikTokOptionsDrawer    tiktokAccounts={tiktokAccounts}      updateAccountOption={updateAccountOption} files={files}/>
                  <YouTubeOptionsDrawer   youtubeAccounts={youtubeAccounts}    updateAccountOption={updateAccountOption}/>
                </>
              }
              userId={user.id}
              user={user}
              isFindingTime={isFindingTime}
              onFindBestTime={handleFindBestTime}
              hashtagSettings={hashtagSettings}
              onHashtagSettingsChange={setHashtagSettings}
            />
          </div>
          <div className='col-md-6 mt-4 mt-md-0'>
            <NetworkPreviewHub
              accounts={selectedAccounts}
              caption={caption}
              files={files}
              postType={postType}
              scheduleDateTime={scheduleDateTime}
            />
          </div>
        </div>

        <PinterestBoardsModal
          show={showPinterestModal}
          onHide={() => { setShowPinterestModal(false); setPinterestHandled(true); }}
          pinterestId={currentPinterestId}
          onBoardSelected={ids => {
            setSelectedBoardIds(ids);
            setShowPinterestModal(false);
            setPinterestHandled(true);
          }}
        />
      </div>
    </>
  );
}